import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Tag, Share2, Facebook, Twitter, Play } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';
import PageSEO from '../components/PageSEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/api/uploads/') || url.startsWith('/api/files/')) return `${BASE_URL}${url}`;
  return url;
};

const getVideoEmbed = (url, type) => {
  if (type === 'youtube') {
    const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([a-zA-Z0-9_-]{11})/);
    return match ? `https://www.youtube.com/embed/${match[1]}` : null;
  }
  if (type === 'vimeo') {
    const match = url.match(/vimeo\.com\/(\d+)/);
    return match ? `https://player.vimeo.com/video/${match[1]}` : null;
  }
  return null;
};

const resolveVideoUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/api/uploads/') || url.startsWith('/api/files/')) return `${BASE_URL}${url}`;
  return url;
};

export const ArticleDetailPage = () => {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedNews, setRelatedNews] = useState([]);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        // Fetch all news and find by ID
        const res = await axios.get(`${API}/news`);
        const found = res.data.find(n => n.id === id);
        if (found) {
          setArticle(found);
          // Get related articles (same category)
          const related = res.data.filter(n => n.id !== id && n.category === found.category).slice(0, 3);
          setRelatedNews(related);
        }
      } catch (error) {
        console.error('Error fetching article:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchArticle();
  }, [id]);

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-12 h-12 border-4 border-green-200 border-t-green-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <h1 className="text-2xl font-bold text-gray-800 mb-4">Article non trouvé</h1>
        <Link to="/medias">
          <Button className="bg-[#0f6b45] text-white">Retour aux actualités</Button>
        </Link>
      </div>
    );
  }

  return (
    <main className="bg-gray-50 min-h-screen">
      <PageSEO
        title={article.title}
        description={article.summary || article.content?.substring(0, 160)}
        path={`/actualites/${id}`}
        image={getImageUrl(article.image_url)}
      />
      {/* Header */}
      <div className="bg-[#0f6b45] py-8">
        <div className="container-main">
          <Link to="/medias" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-4">
            <ArrowLeft size={20} />
            Retour aux actualités
          </Link>
          <h1 className="text-3xl md:text-4xl font-bold text-white">{article.title}</h1>
          <div className="flex flex-wrap items-center gap-4 mt-4">
            <span className="flex items-center gap-2 text-white/80">
              <Calendar size={16} />
              {formatDate(article.created_at)}
            </span>
            {article.category && (
              <span className="flex items-center gap-2 px-3 py-1 bg-white/20 rounded-full text-white text-sm">
                <Tag size={14} />
                {article.category}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container-main py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <article className="lg:col-span-2">
            {/* Video */}
            {article.video_url && (
              <div className="mb-8 rounded-xl overflow-hidden shadow-lg bg-black">
                {article.video_type === 'youtube' || article.video_type === 'vimeo' ? (
                  <div className="aspect-video">
                    <iframe
                      src={getVideoEmbed(article.video_url, article.video_type)}
                      className="w-full h-full"
                      allowFullScreen
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      title={article.title}
                    />
                  </div>
                ) : (
                  <div className="aspect-video">
                    <video
                      src={resolveVideoUrl(article.video_url)}
                      controls
                      poster={getImageUrl(article.image_url) || undefined}
                      className="w-full h-full"
                    />
                  </div>
                )}
              </div>
            )}

            {/* Image (shown only if no video, or as complement) */}
            {article.image_url && !article.video_url && (
              <div className="mb-8 rounded-xl overflow-hidden shadow-lg bg-[#f5f7fa]">
                <img 
                  src={getImageUrl(article.image_url)} 
                  alt={article.title || 'Article de Gandiaye'}
                  loading="lazy"
                  className="w-full"
                  style={{ height: 'auto', objectFit: 'contain' }}
                />
              </div>
            )}

            {/* Summary */}
            {article.summary && (
              <p className="text-xl text-gray-600 font-medium mb-6 leading-relaxed border-l-4 border-[#0f6b45] pl-4">
                {article.summary}
              </p>
            )}

            {/* Content */}
            <div className="prose prose-lg max-w-none">
              <div className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                {article.content}
              </div>
            </div>

            {/* Share */}
            <div className="mt-8 pt-8 border-t border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Share2 size={18} />
                Partager cet article
              </h3>
              <div className="flex gap-3">
                <a 
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center hover:bg-blue-700"
                >
                  <Facebook size={18} />
                </a>
                <a 
                  href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(article.title)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-sky-500 text-white rounded-full flex items-center justify-center hover:bg-sky-600"
                >
                  <Twitter size={18} />
                </a>
              </div>
            </div>
          </article>

          {/* Sidebar */}
          <aside className="space-y-8">
            {/* Related Articles */}
            {relatedNews.length > 0 && (
              <div className="bg-white rounded-xl p-6 shadow-md">
                <h3 className="font-bold text-gray-900 mb-4">Articles similaires</h3>
                <div className="space-y-4">
                  {relatedNews.map((news) => (
                    <Link 
                      key={news.id} 
                      to={`/actualites/${news.id}`}
                      className="block group"
                    >
                      <div className="flex gap-3">
                        {news.image_url && (
                          <img 
                            src={news.image_url} 
                            alt={news.title}
                            className="w-20 h-16 object-cover rounded"
                          />
                        )}
                        <div className="flex-1">
                          <h4 className="font-semibold text-sm text-gray-900 group-hover:text-[#0f6b45] line-clamp-2">
                            {news.title}
                          </h4>
                          <span className="text-xs text-gray-500">{formatDate(news.created_at)}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Contact */}
            <div className="bg-[#0f6b45] rounded-xl p-6 text-white">
              <h3 className="font-bold mb-2">Contact Presse</h3>
              <p className="text-white/80 text-sm mb-4">Pour plus d'informations sur cet article</p>
              <Link to="/contact">
                <Button className="w-full bg-white text-[#0f6b45] hover:bg-gray-100">
                  Nous contacter
                </Button>
              </Link>
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
};

export default ArticleDetailPage;
